import { createContext, useCallback, useContext, useEffect, useState } from "react";

import * as api from "../lib/api";
import { platformName } from "../lib/platforms";
import { formatPost } from "../lib/text";
import { go } from "../lib/navigation";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

export function StoreProvider({ children }) {
  const [dark, setDark] = useState(true);
  const [loading, setLoading] = useState(true);
  const [bootError, setBootError] = useState(null);

  const [documents, setDocuments] = useState([]);
  const [accounts, setAccounts] = useState([]); // [{platform, connected, handle}]
  const [basePrompt, setBasePrompt] = useState("");
  const [platformStyles, setPlatformStyles] = useState({});
  const [settings, setSettings] = useState({
    org_name: "",
    start_date: "",
    end_date: "",
    frequency: "weekly",
  });
  const [posts, setPosts] = useState([]);

  const [kbDirty, setKbDirty] = useState(false);
  const [gen, setGen] = useState({ active: false, status: "", done: 0, total: 0 });
  const [genError, setGenError] = useState(null);
  const [regenId, setRegenId] = useState(null);
  const [sbId, setSbId] = useState(null);
  const [ugcId, setUgcId] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [genModal, setGenModal] = useState(false);
  const [themesBusy, setThemesBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg) => setToast({ msg, id: Date.now() }), []);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setBootError(null);
    try {
      const [docs, accs, prompt, sett, ps] = await Promise.all([
        api.get("/documents"),
        api.get("/accounts"),
        api.get("/prompt"),
        api.get("/settings"),
        api.get("/posts"),
      ]);
      setDocuments(docs);
      setAccounts(accs);
      setBasePrompt(prompt.base_prompt);
      setPlatformStyles(prompt.platform_styles || {});
      setSettings(sett);
      setPosts(ps);
    } catch (e) {
      setBootError(e.message || "Could not reach the backend.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const connectedPlatforms = accounts.filter((a) => a.connected).map((a) => a.platform);

  // ---- Documents ----
  const saveDocument = async (doc) => {
    const payload = {
      title: doc.title,
      type: doc.type,
      content: doc.content,
      is_update: !!doc.is_update,
    };
    const saved =
      doc.id && !doc._isNew
        ? await api.put(`/documents/${doc.id}`, payload)
        : await api.post("/documents", payload);
    setDocuments((p) =>
      p.some((d) => d.id === saved.id) ? p.map((d) => (d.id === saved.id ? saved : d)) : [saved, ...p],
    );
    setKbDirty(true);
    showToast("Document saved");
  };

  const deleteDocument = async (id) => {
    await api.del(`/documents/${id}`);
    setDocuments((p) => p.filter((d) => d.id !== id));
    setKbDirty(true);
    showToast("Document deleted");
  };

  const toggleDocUpdate = async (doc) => {
    const saved = await api.put(`/documents/${doc.id}`, { is_update: !doc.is_update });
    setDocuments((p) => p.map((d) => (d.id === saved.id ? saved : d)));
    setKbDirty(true);
  };

  // Send a file (pdf, docx, xlsx, csv, txt, md) to the backend and get text back.
  const extractDocument = async (file) => api.upload("/documents/extract", file);

  // ---- Accounts ----
  const setAccount = async (platform, patch) => {
    const saved = await api.put(`/accounts/${platform}`, patch);
    setAccounts((p) => p.map((a) => (a.platform === platform ? saved : a)));
  };

  // ---- Prompt ----
  const savePrompt = async (base, styles) => {
    const saved = await api.put("/prompt", { base_prompt: base, platform_styles: styles });
    setBasePrompt(saved.base_prompt);
    setPlatformStyles(saved.platform_styles || {});
  };

  const resetPrompt = async () => {
    const d = await api.get("/prompt/defaults");
    await savePrompt(d.base_prompt, d.platform_styles);
    showToast("Prompt reset to defaults");
  };

  // ---- Settings ----
  const saveSettings = async (patch) => {
    const saved = await api.put("/settings", patch);
    setSettings(saved);
  };

  const saveAudiences = async (audiences) => {
    const saved = await api.put("/settings", { audiences });
    setSettings(saved);
    showToast("Audiences saved");
  };

  // ---- Themes (derived from the knowledge base) ----
  const generateThemes = async () => {
    setThemesBusy(true);
    try {
      const res = await api.post("/themes/generate", {});
      setSettings((s) => ({ ...s, themes: res.themes || [] }));
      showToast("Themes generated from your knowledge base");
      return res.themes || [];
    } catch {
      showToast("Could not generate themes. Check the API key in backend/.env.");
      return null;
    } finally {
      setThemesBusy(false);
    }
  };

  const saveThemes = async (themes) => {
    const res = await api.put("/themes", { themes });
    setSettings((s) => ({ ...s, themes: res.themes || [] }));
    showToast("Themes saved");
    return res.themes || [];
  };

  // ---- Generation ----
  const openGenerate = () => {
    if (!connectedPlatforms.length) {
      go("/accounts");
      showToast("Connect a social account first");
      return;
    }
    if (!documents.length) {
      go("/documents");
      showToast("Add a document first");
      return;
    }
    setGenModal(true);
  };

  const generate = async (focus) => {
    if (!connectedPlatforms.length || !documents.length) {
      openGenerate();
      return;
    }
    setGenModal(false);
    const body = {
      audiences: (focus && focus.audiences) || [],
      themes: (focus && focus.themes) || [],
    };
    go("/calendar");
    setGenError(null);
    setGen({ active: true, status: "Clearing the old calendar", done: 0, total: connectedPlatforms.length });
    try {
      await api.del("/posts");
      setPosts([]);
    } catch {
      /* ignore */
    }

    const all = [];
    const failed = [];
    for (let i = 0; i < connectedPlatforms.length; i++) {
      const pid = connectedPlatforms[i];
      setGen({
        active: true,
        status: `Writing ${platformName(pid)} posts`,
        done: i,
        total: connectedPlatforms.length,
      });
      try {
        const res = await api.post(`/generate/${pid}`, body);
        all.push(...res);
      } catch {
        failed.push(pid);
      }
    }

    setGen({ active: true, status: "Finishing up", done: connectedPlatforms.length, total: connectedPlatforms.length });
    setPosts(all);
    setKbDirty(false);
    setTimeout(() => setGen({ active: false, status: "", done: 0, total: 0 }), 350);

    if (!all.length) {
      setGenError(
        "Generation did not return any posts. Check that ANTHROPIC_API_KEY is set in backend/.env and that ANTHROPIC_MODEL is a model your key can access, then try again.",
      );
    } else if (failed.length) {
      showToast(`Generated ${all.length} posts, ${failed.length} platform(s) failed`);
    } else {
      showToast(`Generated ${all.length} posts`);
    }
  };

  // ---- Posts ----
  const updatePost = async (postObj) => {
    const saved = await api.put(`/posts/${postObj.id}`, postObj);
    setPosts((p) => p.map((x) => (x.id === saved.id ? saved : x)));
    showToast("Post updated");
  };

  const deletePost = async (id) => {
    await api.del(`/posts/${id}`);
    setPosts((p) => p.filter((x) => x.id !== id));
  };

  const regeneratePost = async (postObj) => {
    setRegenId(postObj.id);
    try {
      const saved = await api.post(`/posts/${postObj.id}/regenerate`, {});
      setPosts((p) => p.map((x) => (x.id === saved.id ? saved : x)));
      showToast("Post regenerated");
    } catch {
      showToast("Could not regenerate this post");
    }
    setRegenId(null);
  };

  const copyPost = async (postObj) => {
    try {
      await navigator.clipboard.writeText(formatPost(postObj));
      showToast("Copied to clipboard");
    } catch {
      showToast("Copy failed, select the text manually");
    }
  };

  const generateStoryboard = async (postId) => {
    setSbId(postId);
    try {
      const saved = await api.post(`/posts/${postId}/storyboard`, {});
      setPosts((p) => p.map((x) => (x.id === saved.id ? saved : x)));
      showToast("Storyboard ready");
      return saved;
    } catch {
      showToast("Could not generate the storyboard. Check the API key in backend/.env.");
      return null;
    } finally {
      setSbId(null);
    }
  };

  const saveStoryboard = async (postId, scenes) => {
    const saved = await api.put(`/posts/${postId}`, { id: postId, storyboard: scenes });
    setPosts((p) => p.map((x) => (x.id === saved.id ? saved : x)));
    showToast("Storyboard saved");
    return saved;
  };

  const generateUgc = async (postId) => {
    setUgcId(postId);
    try {
      const saved = await api.post(`/posts/${postId}/ugc`, {});
      setPosts((p) => p.map((x) => (x.id === saved.id ? saved : x)));
      showToast("UGC brief ready");
      return saved;
    } catch {
      showToast("Could not generate the UGC brief. Check the API key in backend/.env.");
      return null;
    } finally {
      setUgcId(null);
    }
  };

  const saveUgc = async (postId, ugc) => {
    const saved = await api.put(`/posts/${postId}`, { id: postId, ugc });
    setPosts((p) => p.map((x) => (x.id === saved.id ? saved : x)));
    showToast("UGC brief saved");
    return saved;
  };

  const exportCalendar = async (format) => {
    try {
      const { blob, filename } = await api.download(`/export?format=${format}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `content-calendar.${format === "docx" ? "docx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      showToast("Could not export. Generate some posts first.");
    }
  };

  const exportItem = async (postId, kind, format) => {
    try {
      const { blob, filename } = await api.download(`/posts/${postId}/export?kind=${kind}&format=${format}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `${kind}.${format === "docx" ? "docx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      showToast("Could not export this item");
    }
  };

  const value = {
    dark,
    setDark,
    loading,
    bootError,
    refreshAll,
    documents,
    saveDocument,
    deleteDocument,
    toggleDocUpdate,
    extractDocument,
    accounts,
    setAccount,
    connectedPlatforms,
    basePrompt,
    platformStyles,
    savePrompt,
    resetPrompt,
    settings,
    saveSettings,
    saveAudiences,
    generateThemes,
    saveThemes,
    themesBusy,
    genModal,
    setGenModal,
    openGenerate,
    posts,
    generate,
    updatePost,
    deletePost,
    regeneratePost,
    copyPost,
    generateStoryboard,
    saveStoryboard,
    sbId,
    generateUgc,
    saveUgc,
    ugcId,
    exportCalendar,
    exportItem,
    selectedDay,
    setSelectedDay,
    regenId,
    kbDirty,
    gen,
    genError,
    setGenError,
    toast,
    showToast,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}